<template>
  <div class="point-card" v-wave="{ disabled: !openable }">
    <component
      :is="href ? 'a' : 'div'"
      class="point"
      :class="{ openable, 'with-actions': showActions.length > 0 }"
      :href="href || undefined"
      :target="href ? (target || undefined) : undefined"
      :rel="href ? (rel || undefined) : undefined"
    >
      <Marker :icon="getMarkerIcon(point)"></Marker>
      <div v-if="kana.enabled && point.extra_fields[kana.key]" class="kana">{{ point.extra_fields[kana.key] }}</div>
      <h4 class="name">{{ point.name }}</h4>

      <div class="detail" :class="{ 'with-actions': showActions.length > 0 }">
        <div class="attributes">
          <slot name="attributes"></slot>
        </div>
      </div>

      <span v-if="shouldShowToPoint" class="to-point" uk-icon="icon:  chevron-right; ratio: 1.0" @click="onToPointClicked"></span>
    </component>

    <div class="actions" v-if="showActions.length > 0" @click.stop @pointerdown.stop>
      <template v-for="action in showActions" :key="getActionKey(action)">
        <a
          v-if="action.href"
          class="action uk-button uk-button-medium"
          :class="{
            [action.class]: true,
            'with-icon': action.icon
          }"
          :id="action.id"
          :href="getActionHref(action, point)"
          :target="action.target"
          v-wave
        >
          <i v-if="action.icon" class="material-symbols-sharp" :data-icon="action.icon"></i>
          <span class="text-modifier">
            {{ getActionLabel(action, messages) }}
          </span>
        </a>

        <button class="action uk-button uk-button-medium"
          :class="{
            [action.class]: true,
            'with-icon': action.icon
          }"
          v-else
          :id="action.id"
          @click="action.onclick && organizationActions[action.onclick]?.(point)"
          v-wave
        >
          <i v-if="action.icon" class="material-symbols-sharp" :data-icon="action.icon"></i>
          <span class="text-modifier">
            {{ getActionLabel(action, messages) }}
          </span>
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Point１つ分
 *
 * 近くの店舗やクラスタ内の店舗に使う想定
 */

import { computed, PropType, ref } from 'vue'
import { useStore } from 'vuex'
import Marker from '@/components/common/Marker.vue'
import { Filters } from '@/services/Filters.ts'
import {
  canRenderAction,
  getActionHref,
  getActionLabel,
  isShowAction,
  type PointAction,
} from '@/utils/pointActions.ts'

/**
 * 一覧ページ用 actions の入れ物
 * storelocator.points[path.id] / storelocator.points.default の両方で使う
 */
interface PointsPageActionsConfig {
  points?: {
    point?: {
      actions?: PointAction[]
    }
  }
}

/**
 * 店舗名のふりがな表示設定
 */
interface PointTitleKana {
  enabled: boolean
  key?: string
}

/**
 * Marker に渡すアイコン
 */
interface MarkerIcon {
  url?: string
  noframe?: boolean
  className?: string
}

const emit = defineEmits<{
  onToPointClicked: [point: Point]
}>()

const props = defineProps({
  /**
   * Point
   */
  point: {
    type: Object as PropType<Point>,
    default: {
      name: null,
      marker: {}
    }
  },
  /**
   * true: 押下可能（店舗ページ有効時など）
   * false のときはクリック波紋（v-wave）も出さない
   */
  openable: {
    type: Boolean,
    default: true
  },
  /**
   * 店舗詳細ページのURL
   * 指定時は店舗情報部分をリンクとして表示する
   */
  href: {
    type: String,
    default: null
  },
  /**
   * 店舗詳細リンクの target
   * href 指定時のみ有効
   */
  target: {
    type: String,
    default: null
  },
  /**
   * 店舗詳細リンクの rel
   * href 指定時のみ有効
   */
  rel: {
    type: String,
    default: null
  },
  /**
   * true: 店舗一覧用actionsを表示対象にする
   */
  actionsEnabled: {
    type: Boolean,
    default: false
  }
})

const store = useStore()
const kana = ref<PointTitleKana>(
  (storelocator as Storelocator & { point_title_kana?: PointTitleKana }).point_title_kana
  || { enabled: false }
)
const messages = computed(() => storelocator.messages)
const organizationActions = computed<Record<string, (point: Point) => void>>(
  () => store.state.organizationActions
)

/**
 * 一覧ページ用 actions
 * design.json の points.points.point.actions が
 * views.py で storelocator.points.default.points.point.actions にマージされる
 * PointsService と同様に path.id があればそちらを優先する
 */
const actions = ref<PointAction[]>(resolvePointsActions())

/**
 * 画面に表示する actions のみを絞り込んだもの
 */
const showActions = computed(() => {
  if (!props.actionsEnabled) {
    return []
  }
  return actions.value.filter(action => (
    isShowAction(action, props.point, storelocator.url_params || {})
    && canRenderAction(action, organizationActions.value)
  ))
})

/**
 * 店舗詳細へ進む矢印を出すか
 */
const shouldShowToPoint = computed(() => props.openable && showActions.value.length === 0)

/**
 * 一覧ページ用 actions の設定を解決する
 */
function resolvePointsActions(): PointAction[] {
  const points = (storelocator.points ?? {}) as unknown as Record<string, PointsPageActionsConfig | undefined>
  const pathActions = storelocator.path?.id
    ? points[storelocator.path.id]?.points?.point?.actions
    : undefined
  if (pathActions) {
    return pathActions
  }
  return points.default?.points?.point?.actions || []
}

/**
 * マーカーのアイコン描画用
 */
function getMarkerIcon(point: Point = props.point): MarkerIcon {
  const marker = Filters.findById(
    Filters.TYPE_MARKERS,
    point.markers[storelocator.lang]
  )

  if (!marker) {
    return {}
  }

  return {
    url: marker.image?.url,
    noframe: marker.image_only,
    className: `icon-${marker.id}`
  }
}

/**
 * Pointに移動するアイコンをピンポイントでクリック時
 *
 * 全体のクリックとアイコンのクリックの割合を把握したいのでわけている
 */
function onToPointClicked(e: Event) {
  e.stopPropagation()

  emit('onToPointClicked', props.point)
}

/**
 * actions の v-for 用キーを返す
 * 追加・削除・並び替えでも同じ action を同一要素として差分更新できるよう、
 * id を優先し、未設定なら必須の label で識別する
 */
function getActionKey(action: PointAction): string {
  return action.id || action.label || ''
}

</script>

<style lang="scss" scoped>
.point-card {
  box-sizing: border-box;
  display: block;
  min-width: 0;
  position: relative;
  width: 100%;
}

.point {
  box-sizing: border-box;
  display: block;
  font-size: .75rem;
  margin: 0 0 0 0;
  max-width: 100%;
  min-width: 0;
  padding: 0.85rem 0.8rem 0.8rem 0.8rem;
  position: relative;
  transition: all .25s;

  &.openable {
    cursor: pointer;
  }

  &.with-actions {
    .name,
    .kana {
      width: calc(100% - 70px);
    }
  }

  .name {
    font-size: .875rem;
    line-height: 20px;
    position: absolute;
    top: 20px;
    left: 58px;
    display: inline-block;
    margin: 1px 0 0 0;
    width: calc(100% - 100px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .kana {
    color: #333;
    line-height: 0.625rem;
    font-size: 0.625rem;
    position: absolute;
    top: 16px;
    left: 58px;
    display: inline-block;
    width: calc(100% - 100px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0.125rem;
    padding-left: 2px;
    + .name {
      top: 26px;
    }
  }

  .to-point {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%) scale(1.5);
    opacity: .3;
  }

  .marker {
    position: relative;
    margin-top: 0 !important;
    transform: none;
  }

  &:hover {
    color: var(--theme-color);
  }
}

// actions の見た目・配置は組織別 style.scss で上書きする想定
// 共通は縦積みの最低限スタイルのみ
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0 0.8rem 46px;
  padding-right: 1rem;

  a, button {
    border: 1px solid var(--text-color);
    border-radius: 15px;
    color: var(--theme-color-tp70);
    flex-grow: 1;
    min-width: 0;
    &:hover {
      background-color: var(--theme-color);
      border: 1px solid var(--theme-color);
      color: var(--text-color);
      transition: all .5s;
      opacity: 1;
    }
    &.with-icon {
      border-radius: 15px;
      padding: 0 2rem 0 1rem;
      position: relative;
      i {
        display: inline;
        position: absolute;
        right: .5rem;
        top: 50%;
        transform: translateY(-50%);
        opacity: .7;
      }
      &:hover {
        i {
          opacity: 1;
          right: .25rem;
          transition: all .5s;
        }
      }
      &.icon-left {
        i {
          left: .5rem;
          right: auto;
        }
        &:hover {
          i {
            left: .25rem;
            right: auto;
          }
        }
      }
      &.icon-inline {
        i {
          position: relative;
          font-size: 1.25rem;
          vertical-align: top;
          display: inline-block;
          transform: translate(0, 4px)
        }
        &:hover {
          i {
            right: .5rem;
          }
        }
      }
    }
  }
}
</style>
